import CircleStyle from "ol/style/Circle";
import FillStyle from "ol/style/Fill";
import RegularShapeStyle from "ol/style/RegularShape";
import StrokeStyle from "ol/style/Stroke";
import Style from "ol/style/Style";
import TextStyle from "ol/style/Text";

import { ObsNetworkId } from "../enums";

const epicenterFill = new FillStyle({
    color: "#b3bdffb3",
});

const epicenterStroke = new StrokeStyle({
    color: "#2942ff",
    width: 1,
});

const epicenterStyleCache = new Map<number, Style>();

/**
 * 震源に応じたスタイルを返す。
 *
 * @param magnitude マグニチュード
 * @returns スタイル
 */
export const calcEpicenterStyle = (magnitude: number | null | undefined) => {
    // マグニチュードに応じて半径を決める
    let radius;
    if (magnitude == null || magnitude < 1) {
        radius = 1;
    } else {
        radius = magnitude * 2;
    }

    const cached = epicenterStyleCache.get(radius);
    if (cached) {
        return cached;
    }

    const style = new Style({
        image: new CircleStyle({
            radius,
            fill: epicenterFill,
            stroke: epicenterStroke,
        }),
    });

    epicenterStyleCache.set(radius, style);

    return style;
};

/**
 * 多角形マーカーの半径にかける係数を計算する。
 *
 * RegularShapeStyleは指定した半径の円に内接する正多角形を表すものであるため、
 * 同じ半径を指定したときその面積は必ずCircleStyleよりも小さくなる。
 * 特に頂点数が少ないときが顕著で、視覚的にかなり小さく見えてしまう。
 * そこで見た目を整えるための補正係数を導入する。
 *
 * strengthが1のとき、円と同一の面積になるよう補正をかける。
 * strengthが0のとき、一切補正をかけない。
 * 1だと逆に多角形のほうが大きく見えやすいため、適当に0.7くらいだと丁度いい。
 *
 * @param n 多角形の頂点の数
 * @returns 補正係数
 */
const calcCorrectionRadio = (n: number) => {
    const strength = 0.7;

    const fullCorrection = Math.sqrt(2 * Math.PI / (n * Math.sin(2 * Math.PI / n)));
    return 1 + (fullCorrection - 1) * strength;
};

const siteStyleDefs = {
    [ObsNetworkId.KNet]: new Style({
        image: new CircleStyle({
            radius: 5,
            stroke: new StrokeStyle({
                width: 2,
                color: "#000",
            }),
            fill: new FillStyle({
                color: "#24d45f",
            }),
        }),
    }),
    [ObsNetworkId.KiKnet]: new Style({
        image: new RegularShapeStyle({
            points: 3,
            radius: 5 * calcCorrectionRadio(3),
            stroke: new StrokeStyle({
                width: 2,
                color: "#000",
            }),
            fill: new FillStyle({
                color: "#24d45f",
            }),
        }),
    }),
} as const;

/**
 * 観測点に応じたスタイルを返す。
 *
 * @param obsNetworkId 観測ネットワークID
 * @returns スタイル
 */
export const calcSiteStyle = (obsNetworkId: ObsNetworkId | null | undefined) => {
    if (obsNetworkId == null) {
        return new Style({
            text: new TextStyle({
                text: "？",
            }),
        });
    }

    return siteStyleDefs[obsNetworkId];
};
